import { getCatalogReference } from '@/mocks/gobeletcupCatalog';
import type { ICatalogReference, IMarkingZone } from '@/mocks/gobeletcupCatalog';
import type { TPrintTechnique } from '@/mocks/gobeletcupPricing';
import type { Product } from '@/domain/product/Product';

// The full-height quadri has two zones behind one option (CFG-10, quote no. 2 of 2026-10-23, and
// Willy's table of 2026-10-26). The customer sees "Quadri grande hauteur" and nothing else; the
// back-office tells the two zones apart:
// - Digital, with a margin above and below: for a small run (1 to 100 copies), and whatever the
//   quantity when the Express option (+30 %) is ticked;
// - IML, the whole printable height: from a larger run (150 to 5,000 copies).
// The zone follows the quantity and the option as they change, and the customer's creation
// follows the zone (usePrintZone). The three smallest references have the IML zone alone: for them
// the Express option gives the quadri small height instead (Willy, 05/11/2026), as they have no
// Digital zone to switch to. Prototype data, CDC domain CFG. Tracked in PROTO-NOTES.md.

type TTallConfiguration = 'digital' | 'iml' | 'short';

// From this quantity the full-height quadri is printed on the whole height (IML), unless Express.
const IML_FROM_QUANTITY = 150;

// What decides the zone: how many copies, and whether the Express option is ticked.
type TTallOrder = { isExpress: boolean; quantity: number };

type TTallZone = { configuration: TTallConfiguration; heightInMm: number; topInMm: number; widthInMm: number };

function hasTallChoice(reference: ICatalogReference | undefined): boolean {
    return Boolean(reference?.quadriTall?.digital);
}

// Which zone holds for a quantity and for the Express option. Digital when Express is ticked or the
// run is small; IML otherwise. Without a Digital zone, Express gives the quadri small height where
// the reference has one, and the IML zone otherwise.
function tallZoneConfiguration(reference: ICatalogReference | undefined, quantity: number, isExpress: boolean): TTallConfiguration {
    if (!hasTallChoice(reference)) {
        return isExpress && reference?.quadriShort ? 'short' : 'iml';
    }
    return isExpress || quantity < IML_FROM_QUANTITY ? 'digital' : 'iml';
}

function zoneOfConfiguration(reference: ICatalogReference | undefined, configuration: TTallConfiguration): IMarkingZone | undefined {
    if (configuration === 'short') {
        return reference?.quadriShort ?? undefined;
    }
    const tall = reference?.quadriTall;
    return tall ? (tall[configuration] ?? tall.iml) : undefined;
}

// The zone in force, or undefined when the style or the reference has none: the wall as the
// backend serves it is then the zone.
function tallZoneFor(reference: ICatalogReference | undefined, technique: TPrintTechnique, order: TTallOrder): TTallZone | undefined {
    if (technique !== 'quadriTall' || !reference?.quadriTall) {
        return undefined;
    }
    const configuration = tallZoneConfiguration(reference, order.quantity, order.isExpress);
    const zone = zoneOfConfiguration(reference, configuration);
    if (!zone) {
        return undefined;
    }
    return { configuration, heightInMm: zone.height, topInMm: zone.marginTop, widthInMm: zone.width };
}

// As the band and the faces of a pitcher: the product is cut to the zone, so the canvas is the zone
// and the 3D view lays it on the wall.
function withTallZone(product: Product, technique: TPrintTechnique, order: TTallOrder): Product {
    const zone = tallZoneFor(getCatalogReference(product.name), technique, order);
    if (!zone) {
        return product;
    }
    return product.with({ height: zone.heightInMm, helpGridRecto: null, helpGridVerso: null, width: zone.widthInMm });
}

type TTallZoneLayout = {
    leftInMm: number;
    surface: ICatalogReference['surface'];
    topInMm: number;
    zone: { height: number; width: number };
};

// Where the zone of a product cut to it lies on the wall; undefined when it is the whole wall.
function tallZoneLayout(reference: ICatalogReference | undefined, productHeightInMm: number): TTallZoneLayout | undefined {
    const tall = reference?.quadriTall;
    if (!reference || !tall) {
        return undefined;
    }
    const zone = [tall.digital, tall.iml, reference.quadriShort].find(
        (candidate) => candidate && Math.abs(candidate.height - productHeightInMm) <= 0.5,
    );
    if (!zone || zone.height >= reference.surface.height - 0.5) {
        return undefined;
    }
    return {
        leftInMm: (reference.surface.width - zone.width) / 2,
        surface: reference.surface,
        topInMm: zone.marginTop,
        zone: { height: zone.height, width: zone.width },
    };
}

export { hasTallChoice, IML_FROM_QUANTITY, tallZoneConfiguration, tallZoneFor, tallZoneLayout, withTallZone };
export type { TTallConfiguration, TTallOrder, TTallZone, TTallZoneLayout };
